import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Camera, CheckCircle, XCircle, Users, UserCheck, ArrowLeft, QrCode } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Html5QrcodeScanner } from 'html5-qrcode';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EventCheckIn = () => {
  const { id: eventId } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scannerActive, setScannerActive] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [lastCheckin, setLastCheckin] = useState(null);
  const [recentCheckins, setRecentCheckins] = useState([]);
  const scannerRef = useRef(null);

  useEffect(() => {
    if (!authLoading && user) {
      fetchEvent();
      fetchStats();
    }
  }, [eventId, user, authLoading]);

  useEffect(() => {
    return () => {
      if (scannerRef.current) {
        scannerRef.current.clear().catch(() => {});
      }
    };
  }, []);

  const fetchEvent = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}`);
      setEvent(response.data);
    } catch (error) {
      toast.error('Event not found');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/checkin-stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const startScanner = () => {
    setScannerActive(true);
    setTimeout(() => {
      const scanner = new Html5QrcodeScanner('qr-reader', {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        rememberLastUsedCamera: true
      });

      scanner.render(
        async (decodedText) => {
          // Extract check-in code from QR data
          const code = decodedText.replace('CHECKIN:', '');
          await handleCheckin(code);
          scanner.pause();
          setTimeout(() => scanner.resume(), 2000);
        },
        (error) => {
          // Ignore scan errors
        }
      );

      scannerRef.current = scanner;
    }, 100);
  };

  const stopScanner = () => {
    if (scannerRef.current) {
      scannerRef.current.clear().catch(() => {});
      scannerRef.current = null;
    }
    setScannerActive(false);
  };

  const handleCheckin = async (code) => {
    try {
      const response = await axios.post(
        `${API}/events/${eventId}/checkin?checkin_code=${encodeURIComponent(code)}`
      );
      
      const result = {
        success: true,
        name: response.data.attendee_name,
        alreadyCheckedIn: response.data.already_checked_in,
        time: new Date().toLocaleTimeString()
      };
      
      setLastCheckin(result);
      setRecentCheckins(prev => [result, ...prev.slice(0, 9)]);
      
      if (result.alreadyCheckedIn) {
        toast.warning(`${result.name} was already checked in`);
      } else {
        toast.success(`${result.name} checked in successfully!`);
      }
      
      fetchStats();
    } catch (error) {
      const result = {
        success: false,
        error: error.response?.data?.detail || 'Invalid code',
        time: new Date().toLocaleTimeString()
      };
      setLastCheckin(result);
      toast.error(result.error);
    }
  };

  const handleManualCheckin = async (e) => {
    e.preventDefault();
    if (!manualCode.trim()) return;
    await handleCheckin(manualCode.trim());
    setManualCode('');
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!event) return null;

  return (
    <div className="min-h-screen bg-secondary/30 py-8">
      <div className="container-custom max-w-4xl mx-auto px-4">
        <button
          onClick={() => navigate(`/events/${eventId}`)}
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-gold mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Event
        </button>

        <div className="flex items-center gap-3 mb-8">
          <QrCode className="w-8 h-8 text-gold" />
          <div>
            <h1 className="text-2xl font-semibold">Event Check-In</h1>
            <p className="text-muted-foreground">{event.title}</p>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-3 gap-4 mb-8">
            <Card>
              <CardContent className="pt-6 text-center">
                <Users className="w-8 h-8 text-gold mx-auto mb-2" />
                <p className="text-3xl font-bold">{stats.total_registered}</p>
                <p className="text-sm text-muted-foreground">Registered</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <UserCheck className="w-8 h-8 text-green-500 mx-auto mb-2" />
                <p className="text-3xl font-bold">{stats.total_checked_in}</p>
                <p className="text-sm text-muted-foreground">Checked In</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-3xl font-bold text-gold">{stats.attendance_rate}%</p>
                <p className="text-sm text-muted-foreground">Attendance</p>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          {/* Scanner Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Camera className="w-5 h-5" />
                QR Scanner
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!scannerActive ? (
                <div className="text-center py-8">
                  <Camera className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                  <p className="text-muted-foreground mb-4">
                    Start the camera to scan attendee QR codes
                  </p>
                  <Button 
                    onClick={startScanner}
                    className="bg-gold text-primary hover:bg-gold/90"
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    Start Scanner
                  </Button>
                </div>
              ) : (
                <div>
                  <div id="qr-reader" className="mb-4" />
                  <Button 
                    onClick={stopScanner}
                    variant="outline"
                    className="w-full"
                  >
                    Stop Scanner
                  </Button>
                </div>
              )}

              {/* Manual Entry */}
              <div className="mt-6 pt-6 border-t">
                <p className="text-sm text-muted-foreground mb-3">Or enter code manually:</p>
                <form onSubmit={handleManualCheckin} className="flex gap-2">
                  <Input
                    value={manualCode}
                    onChange={(e) => setManualCode(e.target.value)}
                    placeholder="Enter check-in code"
                    className="flex-1"
                  />
                  <Button type="submit">Check In</Button>
                </form>
              </div>
            </CardContent>
          </Card>

          {/* Results Section */}
          <Card>
            <CardHeader>
              <CardTitle>Check-In Results</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Last Check-in Result */}
              {lastCheckin && (
                <div className={`p-4 rounded-lg mb-4 ${
                  lastCheckin.success 
                    ? lastCheckin.alreadyCheckedIn 
                      ? 'bg-yellow-100 border border-yellow-300'
                      : 'bg-green-100 border border-green-300'
                    : 'bg-red-100 border border-red-300'
                }`}>
                  <div className="flex items-center gap-3">
                    {lastCheckin.success ? (
                      <CheckCircle className={`w-8 h-8 ${
                        lastCheckin.alreadyCheckedIn ? 'text-yellow-600' : 'text-green-600'
                      }`} />
                    ) : (
                      <XCircle className="w-8 h-8 text-red-600" />
                    )}
                    <div>
                      {lastCheckin.success ? (
                        <>
                          <p className="font-semibold">{lastCheckin.name}</p>
                          <p className="text-sm">
                            {lastCheckin.alreadyCheckedIn ? 'Already checked in' : 'Checked in successfully'}
                          </p>
                        </>
                      ) : (
                        <p className="font-semibold text-red-800">{lastCheckin.error}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Recent Check-ins */}
              <div>
                <p className="text-sm text-muted-foreground mb-3">Recent check-ins:</p>
                {recentCheckins.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">
                    No check-ins yet
                  </p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {recentCheckins.map((checkin, index) => (
                      <div 
                        key={index}
                        className={`flex items-center justify-between p-2 rounded ${
                          checkin.success ? 'bg-secondary/50' : 'bg-red-50'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          {checkin.success ? (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-500" />
                          )}
                          <span className="text-sm">
                            {checkin.success ? checkin.name : checkin.error}
                          </span>
                        </div>
                        <span className="text-xs text-muted-foreground">{checkin.time}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default EventCheckIn;
